import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Clock, LogIn, LogOut, ChevronLeft, User, CheckCircle, AlertCircle, History } from 'lucide-react';
import EmployeeCorrectionsDialog from './EmployeeCorrectionsDialog';
import { madridTime } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PIN_LENGTH = 4;

const TimeClockPage = () => {
  const [pin, setPin] = useState('');
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [todayRecord, setTodayRecord] = useState(null);
  const [showCorrections, setShowCorrections] = useState(false);

  // Update clock every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Clear message after 5 seconds
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const handlePinInput = (digit) => {
    if (pin.length < PIN_LENGTH) {
      setPin(prev => prev + digit);
    }
  };

  const handleBackspace = () => {
    setPin(prev => prev.slice(0, -1));
  };

  const handleClear = () => {
    setPin('');
    setEmployee(null);
    setTodayRecord(null);
    setMessage(null);
  };

  const handleLogin = useCallback(async () => {
    if (pin.length !== PIN_LENGTH) {
      setMessage({ type: 'error', text: `El PIN tiene ${PIN_LENGTH} cifras` });
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/timeclock/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin })
      });

      const data = await res.json();

      if (!res.ok) {
        setMessage({ type: 'error', text: data.detail || 'PIN incorrecto' });
        setPin('');
        return;
      }

      setEmployee(data.employee);
      setTodayRecord(data.today_record);
      setMessage({ type: 'success', text: `¡Hola ${data.employee.name}!` });
    } catch (error) {
      setMessage({ type: 'error', text: 'Error de conexión' });
    } finally {
      setLoading(false);
    }
  }, [pin]);

  // Auto-login once the full 4-digit PIN is typed
  useEffect(() => {
    if (pin.length === PIN_LENGTH && !employee) {
      const timer = setTimeout(() => handleLogin(), 150);
      return () => clearTimeout(timer);
    }
  }, [pin, employee, handleLogin]);

  const handleClockIn = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/timeclock/clock-in`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin })
      });

      const data = await res.json();
      
      if (!res.ok) {
        setMessage({ type: 'error', text: data.detail || 'Error al fichar entrada' });
        return;
      }

      setTodayRecord(data.record);
      setMessage({ type: 'success', text: data.message });
      
      // Play bell sound
      playBellSound();
      
    } catch (error) {
      setMessage({ type: 'error', text: 'Error de conexión' });
    } finally {
      setLoading(false);
    }
  };

  const handleClockOut = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/timeclock/clock-out`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin })
      });

      const data = await res.json();
      
      if (!res.ok) {
        setMessage({ type: 'error', text: data.detail || 'Error al fichar salida' });
        return;
      }

      setMessage({ type: 'success', text: data.message });
      setTodayRecord(prev => ({ ...prev, clock_out: new Date().toISOString(), total_hours: data.total_hours }));
      
      // Play bell sound
      playBellSound();
      
      // Reset after 3 seconds
      setTimeout(() => {
        handleClear();
      }, 3000);
      
    } catch (error) {
      setMessage({ type: 'error', text: 'Error de conexión' });
    } finally {
      setLoading(false);
    }
  };

  // Bell sound for confirmations
  const playBellSound = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const now = ctx.currentTime;
      
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.type = 'sine';
      osc.frequency.setValueAtTime(1318.51, now);
      osc.frequency.exponentialRampToValueAtTime(1200, now + 0.3);
      
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(0.3, now + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.01, now + 1);
      
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      osc.start(now);
      osc.stop(now + 1);
    } catch (e) {
      console.log('Audio not available');
    }
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('es-ES', { timeZone: 'Europe/Madrid', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('es-ES', { timeZone: 'Europe/Madrid', weekday: 'long', day: 'numeric', month: 'long' });
  };

  const formatClockTime = (isoString) => {
    return madridTime(isoString);
  };

  // Determine current state
  const canClockIn = employee && (!todayRecord || (todayRecord.clock_out && !todayRecord.is_closed));
  const canClockOut = employee && todayRecord && todayRecord.clock_in && !todayRecord.clock_out;
  const shiftCompleted = todayRecord && todayRecord.clock_out;

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-900 via-stone-800 to-stone-900 flex flex-col items-center justify-center p-4">
      {/* Header with BIG time */}
      <div className="text-center mb-8">
        <h1 className="text-7xl sm:text-8xl font-bold text-white mb-3 tracking-tight font-mono">
          {formatTime(currentTime)}
        </h1>
        <p className="text-2xl sm:text-3xl text-stone-300 capitalize font-light">{formatDate(currentTime)}</p>
      </div>

      {/* Message Toast */}
      {message && (
        <div className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 px-6 py-3 rounded-full shadow-lg flex items-center gap-2 animate-in slide-in-from-top ${
          message.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
        }`}>
          {message.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
          <span className="font-medium">{message.text}</span>
        </div>
      )}

      {/* Main Card */}
      <Card className="w-full max-w-sm bg-stone-800/50 border-stone-700 backdrop-blur-xl shadow-2xl">
        <CardContent className="p-6">
          {!employee ? (
            /* PIN Entry Screen */
            <div>
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <User className="w-10 h-10 text-amber-400" />
                </div>
                <h2 className="text-2xl font-semibold text-white">Introduce tu PIN</h2>
              </div>

              <div className="flex justify-center gap-3 mb-8" data-testid="pin-display">
                {Array.from({ length: PIN_LENGTH }).map((_, i) => (
                  <div
                    key={i}
                    className={`w-14 h-20 rounded-2xl border-[3px] flex items-center justify-center text-4xl font-bold transition-all ${
                      i < pin.length
                        ? 'border-amber-400 bg-amber-400/20 text-amber-400'
                        : 'border-stone-500 bg-stone-700/50 text-stone-500'
                    }`}
                  >
                    {i < pin.length ? '●' : ''}
                  </div>
                ))}
              </div>

              {/* Number Pad - Bigger and clearer */}
              <div className="grid grid-cols-3 gap-3">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 'C', 0, '⌫'].map((key) => (
                  <Button
                    key={key}
                    variant="ghost"
                    disabled={loading}
                    onClick={() => {
                      if (key === 'C') handleClear();
                      else if (key === '⌫') handleBackspace();
                      else handlePinInput(String(key));
                    }}
                    data-testid={`pin-key-${key}`}
                    className={`h-20 text-4xl font-bold rounded-2xl transition-all active:scale-95 ${
                      key === 'C' 
                        ? 'bg-red-500/30 text-red-400 hover:bg-red-500/40 text-3xl' 
                        : key === '⌫'
                        ? 'bg-stone-600/50 text-stone-300 hover:bg-stone-600 text-3xl'
                        : 'bg-stone-700/70 text-white hover:bg-stone-600'
                    }`}
                  >
                    {key}
                  </Button>
                ))}
              </div>
            </div>
          ) : (
            /* Clock In/Out Screen */
            <div>
              {/* Employee Info */}
              <div className="text-center mb-6">
                <div className="w-20 h-20 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-3xl font-bold text-amber-400">
                    {employee.name.charAt(0).toUpperCase()}
                  </span>
                </div>
                <h2 className="text-2xl font-bold text-white">{employee.name}</h2>
                <p className="text-stone-400 text-sm">{employee.role}</p>
              </div>

              {/* Today's Status */}
              {todayRecord && (
                <div className="bg-stone-700/30 rounded-xl p-4 mb-6">
                  <div className="flex justify-between items-center text-sm">
                    <div className="text-center">
                      <p className="text-stone-400 mb-1">Entrada</p>
                      <p className="text-white font-mono text-lg">
                        {formatClockTime(todayRecord.clock_in)}
                      </p>
                    </div>
                    <Clock className="text-stone-500" size={24} />
                    <div className="text-center">
                      <p className="text-stone-400 mb-1">Salida</p>
                      <p className="text-white font-mono text-lg">
                        {formatClockTime(todayRecord.clock_out)}
                      </p>
                    </div>
                  </div>
                  {shiftCompleted && todayRecord.total_hours && (
                    <div className="mt-3 pt-3 border-t border-stone-600 text-center">
                      <p className="text-amber-400 font-semibold">
                        Total: {todayRecord.total_hours}h trabajadas
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="space-y-3">
                {canClockIn && (
                  <Button
                    onClick={handleClockIn}
                    disabled={loading}
                    className="w-full h-16 text-xl font-bold bg-green-500 hover:bg-green-600 text-white rounded-xl shadow-lg shadow-green-500/30 transition-all active:scale-98"
                    data-testid="clock-in-btn"
                  >
                    <LogIn className="mr-3" size={24} />
                    INICIAR JORNADA
                  </Button>
                )}

                {canClockOut && (
                  <Button
                    onClick={handleClockOut}
                    disabled={loading}
                    className="w-full h-16 text-xl font-bold bg-red-500 hover:bg-red-600 text-white rounded-xl shadow-lg shadow-red-500/30 transition-all active:scale-98"
                    data-testid="clock-out-btn"
                  >
                    <LogOut className="mr-3" size={24} />
                    FINALIZAR JORNADA
                  </Button>
                )}

                {shiftCompleted && (
                  <div className="text-center py-4">
                    <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-2" />
                    <p className="text-green-400 font-semibold">Jornada completada</p>
                  </div>
                )}

                <Button
                  variant="ghost"
                  onClick={() => setShowCorrections(true)}
                  data-testid="employee-mis-registros-btn"
                  className="w-full h-11 text-amber-300 hover:text-amber-100 hover:bg-amber-900/20 rounded-xl border border-amber-900/30"
                >
                  <History className="mr-2" size={18} />
                  Mis registros / Proponer correcci&oacute;n
                </Button>

                <Button
                  variant="ghost"
                  onClick={handleClear}
                  className="w-full h-12 text-stone-400 hover:text-white hover:bg-stone-700/50 rounded-xl"
                >
                  <ChevronLeft className="mr-2" size={20} />
                  Cambiar empleado
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {employee && (
        <EmployeeCorrectionsDialog
          open={showCorrections}
          onClose={() => setShowCorrections(false)}
          employeeId={employee.id}
          pin={pin}
        />
      )}

      {/* Footer */}
      <p className="text-stone-500 text-xs mt-6">
        PONT 10 · Control Horario
      </p>
    </div>
  );
};

export default TimeClockPage;
